import type { BuildingInStoreInfo, ContentsPropertyManagement, PoiContent } from '@/services/dto';
import type { PoiContentSlide } from '@/types/shop';
import { enrichPoiForBuilding, findPoiContentInBuildings } from '@/utils/poi/enrichPoiContent';
import { convertPoiContentToSlide } from '@/utils/shop';

const findBuildingForPoi = (poi: PoiContent, buildings: BuildingInStoreInfo[]) => {
    return (
        buildings.find(
            (building) =>
                building.buildingCode === poi.contents?.cstrTownCd?.['ko'] && building.storeCode === poi.contents?.cstrCd?.['ko']
        ) || buildings.find((building) => building.studioBuildingId === poi.studioBuildingId)
    );
};

const buildSlideFromPoi = (
    poi: PoiContent,
    building: BuildingInStoreInfo,
    language: string,
    buildings: BuildingInStoreInfo[],
    storeContentsPropertyManagement?: ContentsPropertyManagement[]
) => {
    const enrichedPoi = enrichPoiForBuilding(poi, building, storeContentsPropertyManagement);
    const slides = convertPoiContentToSlide(language, buildings, [enrichedPoi]);
    return slides[0] ?? null;
};

const buildMapOnlyPoiContentSlide = (
    selectedPoiId: string | undefined,
    mapPoiDisplayName: string,
    floorName: string,
    buildingName: string,
    language: string
): PoiContentSlide => {
    const localizedName = { ko: mapPoiDisplayName, [language]: mapPoiDisplayName };

    return {
        id: selectedPoiId ?? '',
        name: mapPoiDisplayName,
        buildingName,
        floor: floorName,
        url: '',
        data: {
            id: selectedPoiId ?? '',
            studioId: '',
            studioPoiId: selectedPoiId ?? '',
            studioBuildingId: '',
            studioFloorId: '',
            studioContentsId: '',
            name: localizedName,
            categories: [],
            rootCategories: [],
            managedContents: [],
            contents: {} as PoiContent['contents'],
        },
        poiType: 'normal',
    };
};

export const buildPoiContentSlide = (
    selectedPoiContent: PoiContent | undefined,
    language: string,
    buildings: BuildingInStoreInfo[] | undefined,
    floorName: string,
    buildingName: string,
    selectedPoiId?: string,
    mapPoiDisplayName?: string,
    storeContentsPropertyManagement?: ContentsPropertyManagement[]
): PoiContentSlide | null => {
    if (selectedPoiContent && Array.isArray(buildings) && buildings.length) {
        const building = findBuildingForPoi(selectedPoiContent, buildings);

        if (building) {
            const slide = buildSlideFromPoi(selectedPoiContent, building, language, buildings, storeContentsPropertyManagement);
            if (slide) {
                return slide;
            }
        }
    }

    const studioPoiId = selectedPoiId ?? selectedPoiContent?.studioPoiId;

    if (studioPoiId && Array.isArray(buildings) && buildings.length) {
        const found = findPoiContentInBuildings(studioPoiId, buildings);

        if (found?.poi && found.building) {
            const slide = buildSlideFromPoi(found.poi, found.building, language, buildings, storeContentsPropertyManagement);
            if (slide) {
                return slide;
            }
        }
    }

    if (mapPoiDisplayName) {
        return buildMapOnlyPoiContentSlide(selectedPoiId, mapPoiDisplayName, floorName, buildingName, language);
    }

    return null;
};
